import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../App';

const API = 'https://api.cpms-srlkevcharge.com';

function StatusBadge({ status }) {
  const s = (status || 'offline').toLowerCase();
  return <span className={`badge badge-${s}`}>{status}</span>;
}

function formatDuration(secs) {
  if (!secs) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d + 'Z').toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function ChargerDetail() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { liveEvents, showToast } = useAppContext();

  const [charger, setCharger]   = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [acting, setActing]     = useState(false);

  const fetchData = async () => {
    try {
      const [cRes, sRes] = await Promise.all([
        axios.get(`${API}/chargers/${id}`),
        axios.get(`${API}/chargers/${id}/sessions`)
      ]);
      setCharger(cRes.data);
      setSessions(sRes.data);
    } catch {
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);
  useEffect(() => { if (liveEvents.length > 0) fetchData(); }, [liveEvents]);

  const handleDisconnect = async () => {
    setActing(true);
    try {
      await axios.post(`${API}/chargers/${id}/disconnect`);
      showToast(`Disconnect command sent to ${charger.name}`);
      fetchData();
    } catch (e) {
      showToast(e.response?.data?.detail || 'Failed to send command', 'error');
    } finally {
      setActing(false);
    }
  };

  const handleReconnect = async () => {
    setActing(true);
    try {
      await axios.post(`${API}/chargers/${id}/reconnect`);
      showToast(`${charger.name} set back to operative`);
      fetchData();
    } catch (e) {
      showToast(e.response?.data?.detail || 'Failed to send command', 'error');
    } finally {
      setActing(false);
    }
  };

  const handleStartSession = async () => {
    setActing(true);
    try {
        await axios.post(`${API}/chargers/${id}/start`);
        showToast(`Session started on ${charger.name}`);
        fetchData();
    } catch (e) {
        showToast(e.response?.data?.detail || 'Failed to start session', 'error');
    } finally {
        setActing(false);
    }
  };


  const handleStopSession = async () => {
    setActing(true);
    try {
        await axios.post(`${API}/chargers/${id}/stop`);
        showToast(`Session stopped on ${charger.name}`);
        fetchData();
    } catch (e) {
        showToast(e.response?.data?.detail || 'Failed to stop session', 'error');
    } finally {
        setActing(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove ${charger.name} from the system? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/chargers/${id}`);
      showToast(`${charger.name} removed`);
      navigate('/');
    } catch {
      showToast('Failed to remove charger', 'error');
    }
  };

  if (loading) return <div className="loading">Loading charger...</div>;
  if (!charger) return null;

  const activeSession = sessions.find(s => s.status === 'Active');

  return (
    <>
      <button className="back-link" onClick={() => navigate('/')}>
        ← Back to Overview
      </button>

      {/* Header */}
      <div className="detail-header">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.4px' }}>{charger.name}</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>{charger.location}</p>
        </div>
        <div className="detail-actions">
          <button className="btn btn-primary" onClick={handleStartSession} disabled={acting}>
            Start Session
          </button>
          <button className="btn btn-danger" onClick={handleStopSession} disabled={acting}>
            Stop Session
          </button>
          <button className="btn btn-ghost" onClick={handleDisconnect} disabled={acting}>
            Disconnect
          </button>
          <button className="btn btn-success" onClick={handleReconnect} disabled={acting}>
            Reconnect
          </button>
          <button className="btn btn-danger" onClick={handleRemove}>
            Remove Charger
          </button>
        </div>
      </div>



      {/* Info grid */}
      <div className="info-grid">
        <div className="card info-item">
          <div className="info-label">Status</div>
          <div className="info-value"><StatusBadge status={charger.status} /></div>
        </div>
        <div className="card info-item">
          <div className="info-label">OCPP ID</div>
          <div className="info-value mono">{charger.ocpp_id}</div>
        </div>
        <div className="card info-item">
          <div className="info-label">Connector Type</div>
          <div className="info-value">{charger.connector_type}</div>
        </div>
        <div className="card info-item">
          <div className="info-label">Firmware</div>
          <div className="info-value">{charger.firmware_version || 'Not reported'}</div>
        </div>
        <div className="card info-item">
          <div className="info-label">Last Seen</div>
          <div className="info-value">{formatDate(charger.last_seen)}</div>
        </div>
        <div className="card info-item">
          <div className="info-label">Date Added</div>
          <div className="info-value">{formatDate(charger.date_added)}</div>
        </div>
      </div>

      {/* Active session banner */}
      {activeSession && (
        <div className="card" style={{ padding: '18px 20px', marginBottom: 20, borderLeft: '4px solid var(--blue-light)', background: '#eff6ff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Session Active</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>
                Started {formatDate(activeSession.start_time)}
              </div>
            </div>
            <span className="badge badge-active">Live</span>
          </div>
        </div>
      )}

      {/* Session history */}
      <div className="card">
        <div className="section-header">
          <h2>Session History</h2>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{sessions.length} sessions</span>
        </div>
        <div className="table-wrap">
          {sessions.length === 0 ? (
            <div className="empty-state">
              <h3>No sessions yet</h3>
              <p>Sessions will appear here once this charger connects and starts charging.</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Session ID</th>
                  <th>Started</th>
                  <th>Ended</th>
                  <th>Duration</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.id}>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>#{s.id}</td>
                    <td>{formatDate(s.start_time)}</td>
                    <td>{formatDate(s.end_time)}</td>
                    <td>{formatDuration(s.duration_seconds)}</td>
                    <td><span className={`badge badge-${s.status.toLowerCase()}`}>{s.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
